// AI Service for WISC-V Interpretation Generation
// Integrates with OpenRouter and Gemini APIs

import { API_BASE_URL, DEFAULT_GEMINI_MODEL, GEMINI_FALLBACK_CHAIN } from '../config';
import { responseParser, type AIResponse } from './responseParser';
import {
  buildFallbackCatalog,
  categoriesFor,
  isThinkingCapableGeminiId,
  type GeminiCatalog,
  type GeminiCatalogModel,
} from '../api/catalog/geminiModels';
import {
  AIRequestError,
  classifyGeminiFailure,
  describeGeminiFailure,
} from './geminiFallback';
import { RECOMMENDED_THINKING_BUDGET } from '../components/SimpleWISCV/utils/interpretationPrompt';

/** Generation parameters forwarded to the backend (`generationParams`, see api/services/gemini.ts). */
export interface AIGenerationParams {
  temperature?: number;
  topP?: number;
  topK?: number;
  /** Content budget; for thinking models the backend adds the thinking budget on top. */
  maxTokens?: number;
}

/** Optional request extras for generateWithProvider (8th argument). */
export interface AIRequestOptions {
  /** System instruction (Gemini systemInstruction / OpenAI-compatible system message). */
  systemPrompt?: string;
  generationParams?: AIGenerationParams;
}

export interface GeminiFallbackOptions extends AIRequestOptions {
  /** Thinking settings; applied per model only to thinking-capable ids (Gemini >= 2.5). */
  thinking?: { enabled: boolean; budget: number };
  onChunk?: (chunk: string) => void;
  onStatus?: (status: string) => void;
  /** Backoff before the single 503 retry (default 2000 ms). */
  retryDelayMs?: number;
  /** Injectable for tests. */
  sleep?: (ms: number) => Promise<void>;
}

export interface GeminiFallbackAttempt {
  model: string;
  ok: boolean;
  reason?: string;
}

export interface GeminiFallbackResult {
  response: AIResponse;
  thinking?: string;
  /** Model that actually produced the answer. */
  model: string;
  attempts: GeminiFallbackAttempt[];
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

interface AIConfig {
  models: {
    primary: string;
    fallback: string;
    creative: string;
  };
  maxCostPerRequest: number;
  enableFallback: boolean;
}

class AIService {
  private config: AIConfig;

  constructor() {
    this.log('🔧 AIService: Initializing...');

    this.config = {
      models: {
        primary: (import.meta as any).env?.VITE_DEFAULT_MODEL || 'openai/gpt-4o-mini',
        fallback: (import.meta as any).env?.VITE_FALLBACK_MODEL || 'meta-llama/llama-3.1-8b-instruct:free',
        creative: (import.meta as any).env?.VITE_CREATIVE_MODEL || 'anthropic/claude-3-sonnet'
      },
      maxCostPerRequest: parseFloat((import.meta as any).env?.VITE_MAX_COST_PER_REQUEST || '0.10'),
      enableFallback: (import.meta as any).env?.VITE_ENABLE_FALLBACK_PROVIDERS === 'true'
    };

    // Debug log
    console.log('✅ AIService: Config loaded', {
      primaryModel: this.config.models.primary
    });
  }

  private log(message: string, data?: any): void {
    const isDev = (import.meta as any).env?.DEV;
    const isDebug = (import.meta as any).env?.VITE_LOG_LEVEL === 'debug';

    if (isDev || isDebug) {
      if (data) {
        console.log(message, data);
      } else {
        console.log(message);
      }
    }
  }

  async generateInterpretation(prompt: string): Promise<AIResponse> {
    try {
      // Try with primary model (usually OpenRouter)
      const { response } = await this.generateWithProvider(
        prompt,
        'openrouter', // Default provider logic can be refined if needed
        this.config.models.primary
      );
      return response;
    } catch (error) {
      console.error('Primary AI provider failed:', error);

      if (this.config.enableFallback) {
        console.log('🔄 Trying fallback provider (Gemini)...');
        try {
            // Fallback to Gemini with default fallback model
            const { response } = await this.generateWithProvider(
                prompt,
                'gemini',
                'gemini-2.5-flash' // Hardcoded safe fallback
            );
            return response;
        } catch (fallbackError) {
            console.error('Fallback AI provider failed:', fallbackError);
            throw fallbackError;
        }
      }
      throw error;
    }
  }

  // Generate interpretation with specific model
  async generateInterpretationWithModel(prompt: string, modelId: string): Promise<AIResponse> {
    // Determine provider based on model ID
    const provider = modelId.startsWith('gemini') || modelId.startsWith('models/gemini') ? 'gemini' : 'openrouter';

    const { response } = await this.generateWithProvider(
        prompt,
        provider,
        modelId
    );
    return response;
  }

  // Generate interpretation with streaming
  async generateInterpretationWithStreaming(prompt: string, onChunk: (chunk: string) => void): Promise<AIResponse> {
    try {
      const { response } = await this.generateWithProvider(
        prompt,
        'openrouter',
        this.config.models.primary,
        undefined,
        onChunk
      );
      return response;
    } catch (error) {
      console.error('Streaming generation failed:', error);
       if (this.config.enableFallback) {
        console.log('🔄 Trying fallback provider (Gemini) for streaming...');
         const { response } = await this.generateWithProvider(
            prompt,
            'gemini',
            'gemini-2.5-flash',
             undefined,
             onChunk
        );
        return response;
      }
      throw error;
    }
  }

  // Generate interpretation with streaming and specific model
  async generateInterpretationWithStreamingModel(prompt: string, modelId: string, onChunk: (chunk: string) => void): Promise<AIResponse> {
     const provider = modelId.startsWith('gemini') || modelId.startsWith('models/gemini') ? 'gemini' : 'openrouter';
     const { response } = await this.generateWithProvider(
        prompt,
        provider,
        modelId,
        undefined,
        onChunk
    );
    return response;
  }

  // Parsing delegated to the unified ResponseParser (see ./responseParser.ts)
  private parseResponse(response: string): AIResponse {
    return responseParser.parse(response).sections;
  }

  // Test connection to AI services via backend proxy (CSP compliant)
  async testConnection(signal?: AbortSignal): Promise<{ openrouter: boolean; gemini: boolean }> {
    const results = {
      openrouter: false,
      gemini: false
    };

    // Test both providers in parallel
    const [openrouterResult, geminiResult] = await Promise.allSettled([
      fetch(`${API_BASE_URL}/ai/test/openrouter`, { method: 'POST', signal })
        .then(r => r.ok ? r.json() : null)
        .then(data => data?.connected === true),
      fetch(`${API_BASE_URL}/ai/test/gemini`, { method: 'POST', signal })
        .then(r => r.ok ? r.json() : null)
        .then(data => data?.connected === true),
    ]);

    if (openrouterResult.status === 'fulfilled') {
      results.openrouter = openrouterResult.value || false;
    }
    if (geminiResult.status === 'fulfilled') {
      results.gemini = geminiResult.value || false;
    }

    console.log('🔌 Connection test results:', results);
    return results;
  }

  // Get available models
  async getAvailableModels(): Promise<string[]> {
    const models: string[] = [];

    try {
        const response = await fetch(`${API_BASE_URL}/ai/models/openrouter`);
        if (response.ok) {
            const data = await response.json();
             models.push(...(data.models || []).map((model: any) => model.id));
        }
    } catch (error) {
        console.error('Failed to fetch OpenRouter models:', error);
    }

    return models;
  }

  // Get free models from OpenRouter via backend proxy (CSP compliant)
  async getFreeModels(): Promise<Array<{id: string, name: string, description: string}>> {
    const freeModels: Array<{id: string, name: string, description: string}> = [];

    try {
      const response = await fetch(`${API_BASE_URL}/ai/models/openrouter`);

      if (response.ok) {
        const data = await response.json();

        console.log('📊 getFreeModels: Received', data.models?.length || data.data?.length, 'models from proxy');

        // Backend returns models array directly or in data property
        const modelsList = data.models || data.data || [];

        // Filter for free models if backend returns all models
        const filteredModels = modelsList.filter((model: any) => {
          // If backend already filtered, accept all
          if (!model.pricing) return true;
          return model.pricing.prompt === "0" && model.pricing.completion === "0";
        });

        console.log('✅ getFreeModels: Using', filteredModels.length, 'free models');

        freeModels.push(...filteredModels.map((model: any) => ({
          id: model.id,
          name: model.name || model.id,
          description: model.description || 'No description available'
        })));
      } else {
        console.warn('⚠️ getFreeModels: HTTP response not ok, no OpenRouter models available');
      }
    } catch (error) {
      console.error('Failed to fetch free OpenRouter models:', error);
    }

    // No hardcoded fallback list: the old one proposed ids that no longer exist in the
    // catalog (e.g. meta-llama/llama-3.3-70b-instruct:free). Empty = "no models" in the UI.
    return freeModels;
  }

  /**
   * Gemini model catalog via backend (GET /api/models/gemini): live ListModels,
   * categorized, newest first. Never throws — on any error (offline, old backend,
   * malformed payload) returns the shared static fallback (source: "fallback").
   */
  async getGeminiCatalog(): Promise<GeminiCatalog> {
    try {
      const response = await fetch(`${API_BASE_URL}/models/gemini`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (!data || !Array.isArray(data.models) || data.models.length === 0) {
        throw new Error('empty Gemini catalog');
      }
      const models = data.models.filter(
        (m: any) => m && typeof m.id === 'string' && typeof m.name === 'string'
      ) as GeminiCatalogModel[];
      if (models.length === 0) throw new Error('no valid Gemini models');
      return {
        fetchedAt: typeof data.fetchedAt === 'string' ? data.fetchedAt : null,
        source: data.source === 'api' || data.source === 'cache' ? data.source : 'fallback',
        categories: Array.isArray(data.categories) && data.categories.length > 0 ? data.categories : categoriesFor(models),
        models,
      };
    } catch (error) {
      console.warn('⚠️ getGeminiCatalog: using static fallback list:', error);
      return buildFallbackCatalog();
    }
  }

  /** Flat list of Gemini models (catalog order: category, then newest first). */
  async getGeminiModels(): Promise<GeminiCatalogModel[]> {
    return (await this.getGeminiCatalog()).models;
  }

  // Generate with explicit provider choice via backend proxy (CSP compliant)
  async generateWithProvider(
    prompt: string,
    provider: 'openrouter' | 'gemini',
    modelId?: string,
    thinkingConfig?: { enabled: boolean; budget: number },
    onChunk?: (chunk: string) => void,
    onStatus?: (status: string) => void,
    fallbackModel?: string,
    requestOptions?: AIRequestOptions
  ): Promise<{ response: AIResponse; thinking?: string; model?: string }> {
    console.log('🎯 generateWithProvider via PROXY: Called with:', {
      provider,
      modelId,
      thinkingEnabled: thinkingConfig?.enabled,
      thinkingBudget: thinkingConfig?.budget,
      streaming: !!onChunk
    });

    // OpenRouter has no default model (free ids disappear from the catalog) — the user
    // must pick one from the live list; Gemini falls back to DEFAULT_GEMINI_MODEL.
    if (provider === 'openrouter' && !modelId) {
      throw new AIRequestError('Nie wybrano modelu OpenRouter — wybierz model z listy albo użyj Gemini.');
    }
    let model = modelId || DEFAULT_GEMINI_MODEL;

    // VALIDATION: Ensure model matches provider
    const isGeminiModel = model.startsWith('gemini-') || model.startsWith('models/gemini');
    // const isOpenRouterModel = model.includes('/') && !model.startsWith('models/'); // Unused

    if (provider === 'gemini' && !isGeminiModel) {
      console.warn(`⚠️ Model mismatch: "${model}" is not a Gemini model. Using default: ${DEFAULT_GEMINI_MODEL}`);
      model = DEFAULT_GEMINI_MODEL;
      onStatus?.(`⚠️ Model niezgodny z Gemini - używam ${DEFAULT_GEMINI_MODEL}`);
    } else if (provider === 'openrouter' && isGeminiModel) {
      throw new AIRequestError(`Model „${model}” to model Gemini — wybierz model z listy OpenRouter albo przełącz dostawcę na Gemini.`);
    }

    onStatus?.(`🤖 Łączę z ${provider === 'gemini' ? 'Gemini' : 'OpenRouter'} (${model})...`);

    try {
      if (onChunk) {
        // STREAMING via proxy
        console.log('🌊 Using streaming via /api/ai/stream');
        const response = await fetch(`${API_BASE_URL}/ai/stream`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            prompt,
            provider,
            model,
            thinkingConfig,
            generationParams: requestOptions?.generationParams,
            systemPrompt: requestOptions?.systemPrompt
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          throw new AIRequestError(errorData.error || `HTTP ${response.status}`, response.status);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let buffer = '';
        let fullText = '';
        let thinkingText = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              if (data === '[DONE]') continue;

              // JSON.parse in its OWN try/catch — a malformed chunk is
              // skipped, but errors thrown by the handlers below must NOT
              // be swallowed (previously a backend {error} event was
              // silently eaten here and the UI reported a fake success).
              let parsed: { content?: string; thinking?: string; error?: string; warning?: string };
              try {
                parsed = JSON.parse(data);
              } catch {
                // Skip invalid JSON lines
                continue;
              }

              // Backend signalled a mid-stream error — propagate to caller.
              if (parsed.error) {
                throw new AIRequestError(parsed.error);
              }
              // Backend warning (e.g. truncated response) — surface in DebugLog.
              if (parsed.warning) {
                onStatus?.(`⚠️ ${parsed.warning}`);
              }
              if (parsed.content) {
                fullText += parsed.content;
                onChunk(parsed.content);
              }
              if (parsed.thinking) {
                thinkingText += parsed.thinking;
              }
            }
          }
        }

        // Guard: stream finished without any content — never return an
        // empty "success" (e.g. model spent the whole budget on thinking).
        if (fullText.trim() === '') {
          throw new AIRequestError('Strumień zakończony bez treści — spróbuj ponownie lub zmień model');
        }

        return {
          response: this.parseResponse(fullText),
          thinking: thinkingText || undefined
        };
      } else {
        // NON-STREAMING via proxy
        console.log('📤 Using non-streaming via /api/ai/generate');
        const response = await fetch(`${API_BASE_URL}/ai/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            prompt,
            provider,
            model,
            thinkingConfig,
            generationParams: requestOptions?.generationParams,
            systemPrompt: requestOptions?.systemPrompt
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          throw new AIRequestError(errorData.error || `HTTP ${response.status}`, response.status);
        }

        const data = await response.json();
        return {
          response: this.parseResponse(data.content || data.text || ''),
          thinking: data.thinking
        };
      }
    } catch (error: unknown) {
      console.error('❌ generateWithProvider error:', error);

      let errorMessage = '';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null && 'message' in error) {
        errorMessage = String((error as any).message);
      } else {
        errorMessage = String(error);
      }

      // Fallback to Gemini if OpenRouter fails with 429: the Gemini chain starting at
      // the default model (see config.ts), with the same system prompt and parameters.
      if (provider === 'openrouter' && errorMessage.includes('429') && fallbackModel) {
        console.warn('⚠️ OpenRouter rate limited, switching to the Gemini chain');
        onStatus?.(`⚠️ OpenRouter zajęty - przełączam na ${DEFAULT_GEMINI_MODEL}...`);

        const { response, thinking, model: answeredBy } = await this.generateWithGeminiFallback(
          prompt,
          [DEFAULT_GEMINI_MODEL, ...GEMINI_FALLBACK_CHAIN.filter((id) => id !== DEFAULT_GEMINI_MODEL)],
          // Pinned thinking budget (a dynamic one can eat the whole output limit).
          { ...requestOptions, onChunk, onStatus, thinking: { enabled: true, budget: RECOMMENDED_THINKING_BUDGET } }
        );
        // `model` = who actually answered, so the UI does not name the OpenRouter model.
        return { response, thinking, model: answeredBy };
      }

      throw error;
    }
  }

  /**
   * Gemini generation with a fallback chain (see services/geminiFallback.ts):
   * each model is tried in order; an upstream 503 is retried once after a short
   * backoff, 429/500/empty answers move straight to the next model, a 400 (or any
   * other non-transient error) stops immediately. A model that already streamed
   * content is never replaced mid-answer (two reports would be concatenated) — the
   * error propagates and the UI keeps the partial text.
   * Returns which model actually answered, for the UI notice.
   */
  async generateWithGeminiFallback(
    prompt: string,
    chain: string[],
    options: GeminiFallbackOptions = {}
  ): Promise<GeminiFallbackResult> {
    const { thinking, onChunk, onStatus, retryDelayMs = 2000, sleep = defaultSleep, systemPrompt, generationParams } = options;
    const models = chain.length > 0 ? chain : [DEFAULT_GEMINI_MODEL];
    const attempts: GeminiFallbackAttempt[] = [];
    let lastError: unknown = null;

    for (let i = 0; i < models.length; i++) {
      const model = models[i];
      const thinkingConfig = thinking?.enabled && isThinkingCapableGeminiId(model)
        ? { enabled: true, budget: thinking.budget }
        : undefined;

      for (let attempt = 1; attempt <= 2; attempt++) {
        let contentSeen = false;
        const trackedOnChunk = onChunk
          ? (chunk: string) => {
              if (chunk.trim() !== '') contentSeen = true;
              onChunk(chunk);
            }
          : undefined;
        try {
          const result = await this.generateWithProvider(
            prompt,
            'gemini',
            model,
            thinkingConfig,
            trackedOnChunk,
            onStatus,
            undefined,
            { systemPrompt, generationParams }
          );
          attempts.push({ model, ok: true });
          return { ...result, model, attempts };
        } catch (error) {
          lastError = error;
          const reason = describeGeminiFailure(error);
          attempts.push({ model, ok: false, reason });
          const action = contentSeen ? 'stop' : classifyGeminiFailure(error, { isFallback: i > 0 });
          if (action === 'stop') throw error;
          if (action === 'retry' && attempt === 1) {
            onStatus?.(`⏳ ${model}: ${reason} — ponawiam za ${Math.round(retryDelayMs / 1000)} s...`);
            await sleep(retryDelayMs);
            continue;
          }
          const next = models[i + 1];
          if (next) onStatus?.(`⚠️ ${model}: ${reason} — przełączam na model zapasowy ${next}...`);
          break;
        }
      }
    }

    const summary = attempts.map((a) => `${a.model}: ${a.reason}`).join('; ');
    const lastMessage = lastError instanceof Error ? lastError.message : String(lastError);
    throw new AIRequestError(
      `Żaden model Gemini nie odpowiedział (${summary}). Ostatni błąd: ${lastMessage}`,
      lastError instanceof AIRequestError ? lastError.status : null
    );
  }

  // Analyze WISC-V Card with OCR (Gemini Vision)
  async analyzeWISCVCard(imageBase64: string): Promise<{
    subtestScores: Record<string, string>;
    compositeScores: Record<string, string>;
  }> {

    // Remove data URL prefix if present
    const base64Data = imageBase64.includes(',')
      ? imageBase64.split(',')[1]
      : imageBase64;

    const ocrPrompt = `Przeanalizuj ten skan protokołu WISC-V i wydobądź WSZYSTKIE wyniki.

WAŻNE: Zwróć TYLKO poprawny JSON bez dodatkowego tekstu.

FORMAT ODPOWIEDZI:
{
  "subtestScores": {
    "block_design": "wartość lub puste",
    "similarities": "wartość lub puste",
    "matrix_reasoning": "wartość lub puste",
    "digit_span": "wartość lub puste",
    "coding": "wartość lub puste",
    "vocabulary": "wartość lub puste",
    "figure_weights": "wartość lub puste",
    "visual_puzzles": "wartość lub puste",
    "picture_span": "wartość lub puste",
    "symbol_search": "wartość lub puste"
  },
  "compositeScores": {
    "vci": "wartość lub puste",
    "vsi": "wartość lub puste",
    "fri": "wartość lub puste",
    "wmi": "wartość lub puste",
    "psi": "wartość lub puste",
    "fsiq": "wartość lub puste"
  }
}

INSTRUKCJE:
1. Znajdź wyniki podtestów (zazwyczaj 1-19)
2. Znajdź wskaźniki złożone (zazwyczaj 40-160)
3. Jeśli jakiegoś wyniku nie ma na obrazie, wpisz pustą wartość ""
4. Zwróć TYLKO JSON, bez markdown ani wyjaśnień`;

    try {
      const response = await fetch(`${API_BASE_URL}/ai/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            prompt: ocrPrompt,
            provider: 'gemini',
            model: 'gemini-2.5-flash',
            image: base64Data
          })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Gemini Vision API error: ${response.status} - ${errorData.error}`);
      }

      const data = await response.json();
      const textResponse = data.content || '';

      // Try to extract JSON from response
      const jsonMatch = textResponse.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('Nie udało się wydobyć JSON z odpowiedzi OCR');
      }

      const parsed = JSON.parse(jsonMatch[0]);
      return {
        subtestScores: parsed.subtestScores || {},
        compositeScores: parsed.compositeScores || {}
      };
    } catch (error: any) {
      console.error('OCR analysis failed:', error);
      throw new Error(`Błąd analizy OCR: ${error.message}`);
    }
  }

  // Set selected model
  setSelectedModel(modelId: string) {
    this.config.models.primary = modelId;
  }

  // Public wrapper for parseResponse - used by UI for resilience fallback
  parseResponsePublic(response: string): AIResponse {
    return this.parseResponse(response);
  }
}

export default new AIService();